import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { ScanLine, Eye, Trash2 } from 'lucide-react';
import { format } from 'date-fns';
import { fmtDate } from '@/lib/dateFmt';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function ScansioniPage() {
  const [scans, setScans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cleanupOpen, setCleanupOpen] = useState(false);

  useEffect(() => { fetchScans(); }, []);

  const fetchScans = async () => {
    try {
      const res = await axios.get(`${API}/worker-scans`, { withCredentials: true });
      setScans(res.data);
    } catch (error) { toast.error('Errore'); }
    finally { setLoading(false); }
  };

  const handleStatusUpdate = async (id, status) => {
    try {
      await axios.put(`${API}/worker-scans/${id}/status?status=${status}`, {}, { withCredentials: true });
      toast.success('Stato aggiornato');
      fetchScans();
    } catch (error) { toast.error('Errore'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminare questa scansione?')) return;
    try {
      await axios.delete(`${API}/worker-scans/${id}`, { withCredentials: true });
      toast.success('Scansione eliminata');
      fetchScans();
    } catch (error) { toast.error('Errore eliminazione'); }
  };

  const handleCleanupAll = async () => {
    try {
      const r = await axios.post(`${API}/worker-scans/cleanup-all`, {}, { withCredentials: true });
      toast.success(r.data.message || 'Pulizia completata');
      setCleanupOpen(false);
      fetchScans();
    } catch (error) { toast.error('Errore pulizia'); }
  };

  const statusColors = {
    inviato: 'bg-amber-100 text-amber-800 border-amber-200',
    visionato: 'bg-blue-100 text-blue-800 border-blue-200',
    archiviato: 'bg-emerald-100 text-emerald-800 border-emerald-200'
  };
  const typeLabels = { ddt: 'DDT', scontrino: 'Scontrino', fattura: 'Fattura', altro: 'Altro' };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div></div>;

  return (
    <div className="space-y-6" data-testid="scansioni-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900">Scansioni Operai</h1>
          <p className="text-slate-500 mt-1">Documenti scannerizzati e inviati dagli operai</p>
        </div>
        {scans.length > 0 && (
          <AlertDialog open={cleanupOpen} onOpenChange={setCleanupOpen}>
            <AlertDialogTrigger asChild>
              <Button variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" data-testid="cleanup-all-scansioni-btn">
                <Trash2 className="h-4 w-4 mr-2" />Elimina tutte
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Eliminare tutte le scansioni?</AlertDialogTitle>
                <AlertDialogDescription>
                  Verranno rimosse <strong>{scans.length} scansioni</strong>. Questa azione è irreversibile.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Annulla</AlertDialogCancel>
                <AlertDialogAction onClick={handleCleanupAll} className="bg-red-600 hover:bg-red-700" data-testid="confirm-cleanup-scansioni">Elimina tutte</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
      </div>

      <Card className="border-slate-200">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="text-xs uppercase tracking-wider text-slate-500">Operaio</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500">Titolo</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500">Tipo</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500">Data</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500">Stato</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-right">Azioni</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {scans.length === 0 ? (
                <TableRow><TableCell colSpan={6} className="text-center py-12 text-slate-500"><ScanLine className="h-10 w-10 mx-auto text-slate-300 mb-3" />Nessuna scansione ricevuta</TableCell></TableRow>
              ) : scans.map((scan) => (
                <TableRow key={scan.id} className="border-slate-100" data-testid={`scan-row-${scan.id}`}>
                  <TableCell className="font-medium text-slate-900">{scan.worker_name}</TableCell>
                  <TableCell>
                    <div><p className="text-slate-900">{scan.title}</p>{scan.description && <p className="text-xs text-slate-500 truncate max-w-xs">{scan.description}</p>}</div>
                  </TableCell>
                  <TableCell><Badge variant="outline" className="text-xs capitalize">{typeLabels[scan.scan_type] || scan.scan_type}</Badge></TableCell>
                  <TableCell className="text-slate-600">{fmtDate(scan.created_at, 'dd-MM-yyyy HH:mm')}</TableCell>
                  <TableCell>
                    <Select value={scan.status} onValueChange={(v) => handleStatusUpdate(scan.id, v)}>
                      <SelectTrigger className="w-28 h-8"><Badge className={statusColors[scan.status]} variant="outline">{scan.status}</Badge></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="inviato">Inviato</SelectItem>
                        <SelectItem value="visionato">Visionato</SelectItem>
                        <SelectItem value="archiviato">Archiviato</SelectItem>
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`preview-scan-${scan.id}`}><Eye className="h-4 w-4" /></Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-red-600 hover:bg-red-50" onClick={() => handleDelete(scan.id)} data-testid={`delete-scan-${scan.id}`}><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
